import { useContext, useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { HighchartsReactRefObject } from '@highcharts/react'
import { Theme } from '@mui/material'
import { sub } from 'date-fns'
import { SVGElement } from 'highcharts'
import { useSnackbar } from 'notistack'

import { AppContext, useHost } from '../../../../providers/app-provider'
import api from '../../../../utils/api'
import { getFormattedDateTime } from '../../../../utils/helpers'
import useTemperatures from '../../../../utils/hooks/useTemperatures'
import DeviceContext from '../../context'

export const defaultDataProps = {
  chart: {
    height: 500,
    width: 960,
  },
  title: {
    align: 'center',
  },
  rangeSelector: {
    selected: 1,
  },
  legend: {
    enabled: true,
  },
  tooltip: {
    shared: true,
  },
  plotOptions: {
    series: {
      marker: {
        enabled: true,
        radius: 4,
      },
      showInNavigator: true,
    },
  },
  accessibility: {
    enabled: true,
  },
}

export const lambdaChannelMatrix: { [key: string]: string[] } = {
  rssi: ['cell_con_flag'],
  batt: ['batt_count'],
  hum: ['fws_alert_ind', 'hmd_alert_ind'],
  temp: ['fla_ind', 'fla_score', 'hat_alert_ind', 'refrigeration_alert_ind', 'anomaly_count', 'anomaly_ref'],
}

export const cleanData = ({
  apiRows,
  channel,
  formatTemperature,
}: {
  apiRows: { category: string; [x: string]: unknown }[]
  channel: ChannelType
  formatTemperature: (temp: number, rounding?: 'tenths' | 'integer', fromUnit?: string, showUnit?: boolean) => string | number
}) => {
  const valueField = channel.channelName
  const isDBm = channel.unit === 'dBm'
  const isCelsius = channel.unit === '°C'

  const channelData = apiRows
    ?.map((d) => {
      let value = d[valueField]

      if (typeof value === 'number') {
        value = Math.round(value * 10) / 10
        if (isDBm) value = Math.abs(value as number)
        if (isCelsius) value = formatTemperature(value as number, 'tenths', 'degF', false)
      }

      return [new Date(d.category).getTime(), value] as [number, unknown]
    })
    ?.filter(([timestamp]) => !Number.isNaN(timestamp)) // Ensure valid timestamps
    ?.sort((a, b) => a[0] - b[0])

  return channelData?.filter((d, i, arr) => !((d[0] === arr[i + 1]?.[0] && d[1] === null) || (d[0] === arr[i - 1]?.[0] && d[1] === null)))
}

export const useFetchChartData = (deviceId: number, channels: ChannelType[], createdAt: string, chart: string) => {
  const { enqueueSnackbar } = useSnackbar()
  const auth = useAuth()
  const host = useHost()
  const { pageRefreshCounter } = useContext(AppContext)
  const { charts, setCharts } = useContext(DeviceContext)
  const { formatTemperature } = useTemperatures()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isBackgroundLoading, setIsBackgroundLoading] = useState<boolean>(false)
  const [timeRangeLoaded, setTimeRangeLoaded] = useState<'3m' | '6m' | 'all'>('3m')

  // Memoize the channels to prevent unnecessary fetchData calls
  const stableChannels = JSON.stringify(channels)
  const memoizedChannels: ChannelType[] = useMemo(() => JSON.parse(stableChannels), [stableChannels])

  const fetchData = async (timeRange: '3m' | '6m' | 'all' = '3m') => {
    if (!host || !auth.user?.access_token) return
    setIsBackgroundLoading(true)
    setTimeRangeLoaded(timeRange)
    const newData: IChartData[] = []
    const channelsList = memoizedChannels.map((c) => c.channelName).join('&channels=')
    const startTime = timeRange === 'all' ? new Date(createdAt).toJSON() : sub(new Date(), { months: timeRange === '6m' ? 6 : 3 }).toJSON()

    const getChartDataApi = async (offset = 0) => {
      if (!host || !auth.user?.access_token) return
      const res = await api.get(
        `${host}/api/v2/central/devices/${deviceId}/generic-chart?startTime=${startTime}&endTime=${new Date().toJSON()}&channels=${channelsList}&limit=250000&offset=${offset}`
      )

      if (!res.ok) {
        enqueueSnackbar('Failed to fetch data', { variant: 'error' })
        return
      }

      memoizedChannels.forEach((channel) => {
        const channelData = cleanData({ apiRows: res.body.rows, channel, formatTemperature })
        const existingData = newData.find((d) => d.channelName === channel.channelName)
        if (existingData) {
          existingData.data.push(...channelData)
        } else {
          newData.push({
            channelName: channel.channelName,
            yAxisTitle: channel.yAxisTitle,
            seriesName: channel.seriesName,
            unit: channel.unit,
            data: channelData,
          })
        }
      })

      if (res.body.hasMore) {
        await getChartDataApi(offset + 250000) // Continue fetching more data
      }
    }

    await getChartDataApi() // Initial fetch
    setCharts((prev) => ({
      ...prev,
      [chart]: { data: newData, includesAll: timeRange === 'all' },
    }))

    setIsLoading(false)
    setIsBackgroundLoading(false)
  }

  const fetchInitialData = useEffectEvent(fetchData)
  useEffect(() => {
    if (isLoading) fetchInitialData()
  }, [isLoading, chart, createdAt, deviceId, formatTemperature, memoizedChannels, host, pageRefreshCounter])

  return { data: charts[chart]?.data ?? [], isLoading, isBackgroundLoading, fetchData, timeRangeLoaded }
}

const getChartAlerts = ({ alerts, channels }: { alerts: IAlertHistory[]; channels: ChannelType[] }) => {
  const channelNames = new Set(channels.map((c) => c.channelName))
  return alerts.filter((a) => channelNames.has(a.channel) || [...channelNames].some((name) => lambdaChannelMatrix[name]?.includes(a.channel)))
}

interface CustomTooltip extends SVGElement {
  isMouseOverTooltip: () => boolean
  isMouseOverPlotband: () => boolean
  setPlotbandHover: (hover: boolean) => void
}

interface ExtendedChart extends Highcharts.Chart {
  customTooltip?: CustomTooltip | null
}
export const getAlertPlotBands = ({
  deviceId,
  alerts,
  channels,
  chartRef,
  theme,
}: {
  deviceId: number
  alerts: IAlertHistory[]
  channels: ChannelType[]
  chartRef: React.RefObject<HighchartsReactRefObject | null>
  theme: Theme
}) => {
  const chartAlerts = getChartAlerts({ alerts, channels })

  return chartAlerts.flatMap((a) => {
    const enterEvent = a.events.find((e) => e.event === 'enter')
    const exitEvent = a.events.find((e) => e.event === 'exit')

    const enterDate = enterEvent?.updatedAt ?? 0
    const exitDate = exitEvent?.updatedAt ?? (a.status !== 'Resolved' ? new Date().toJSON() : undefined)

    if (!exitDate) return [] // Skip invalid data

    return {
      color: a.status === 'Resolved' ? theme.palette.action.selected : theme.palette.warning.lightShades['30p'],
      from: new Date(enterDate).getTime(),
      to: new Date(exitDate).getTime(),
      events: {
        mouseover(e: MouseEvent) {
          if (!chartRef.current) return
          const { chart } = chartRef.current as { chart: ExtendedChart }

          // Remove existing custom tooltip
          if (chart.customTooltip) {
            chart.customTooltip.destroy()
          }

          // Create custom tooltip using chart renderer
          const tooltipText = `${a.status} Alert<br />${a.ruleName}<br />${getFormattedDateTime(enterDate.toString())} - ${getFormattedDateTime(exitDate.toString())}<br />
                  <a href="/devices/${deviceId}?tab=alerts&alertId=${a.alertId}" target="_blank" style="color:#2FAAFF">[View Alert]</a>`

          // Calculate safe positioning to avoid chart edges
          const tooltipWidth = 250 // Estimated tooltip width
          const tooltipHeight = 100 // Estimated tooltip height
          const margin = 20 // Safety margin from edges

          let x = e.offsetX
          let y = e.offsetY - 80

          // Adjust horizontal position if too close to right edge
          if (x + tooltipWidth > chart.plotLeft + chart.plotWidth) {
            x = chart.plotLeft + chart.plotWidth - tooltipWidth - margin
          }

          // Adjust horizontal position if too close to left edge
          if (x < chart.plotLeft + margin) {
            x = chart.plotLeft + margin
          }

          // Adjust vertical position if too close to top edge
          if (y < chart.plotTop + margin) {
            y = e.offsetY + 20 // Show below mouse instead of above
          }

          // Adjust vertical position if too close to bottom edge
          if (y + tooltipHeight > chart.plotTop + chart.plotHeight) {
            y = chart.plotTop + chart.plotHeight - tooltipHeight - margin
          }

          const tooltip = chart.renderer
            .label(tooltipText, x, y, 'callout', undefined, undefined, true)
            .attr({
              fill: 'rgba(0, 0, 0, 0.8)',
              padding: 10,
              r: 5,
              zIndex: 10,
            })
            .css({
              color: 'white',
              fontSize: '12px',
              fontWeight: 'normal',
            })
            .add()

          // Add mouse tracking for both tooltip and plotband
          let isMouseOverTooltip = false
          let isMouseOverPlotband = true // Mouse is over plotband when this event fires

          tooltip.on('mouseenter', () => {
            isMouseOverTooltip = true
          })

          tooltip.on('mouseleave', () => {
            isMouseOverTooltip = false
            // Only destroy tooltip if mouse is not over plotband either
            setTimeout(() => {
              if (chart.customTooltip && !isMouseOverTooltip && !isMouseOverPlotband) {
                chart.customTooltip.destroy()
                chart.customTooltip = null
              }
            }, 200)
          })

          // Store reference to tooltip for cleanup with both hover states
          chart.customTooltip = tooltip as CustomTooltip
          chart.customTooltip.isMouseOverTooltip = () => isMouseOverTooltip
          chart.customTooltip.isMouseOverPlotband = () => isMouseOverPlotband
          chart.customTooltip.setPlotbandHover = (hover: boolean) => {
            isMouseOverPlotband = hover
          }
        },
        mouseout() {
          const { chart } = chartRef.current as { chart: ExtendedChart }
          if (chart.customTooltip) {
            // Mark that mouse left the plotband
            chart.customTooltip.setPlotbandHover?.(false)

            setTimeout(() => {
              // Only destroy if mouse is not over tooltip or plotband
              const isOverTooltip = chart.customTooltip?.isMouseOverTooltip?.()
              const isOverPlotband = chart.customTooltip?.isMouseOverPlotband?.()

              if (chart.customTooltip && !isOverTooltip && !isOverPlotband) {
                chart.customTooltip.destroy()
                chart.customTooltip = null
              }
            }, 200)
          }
        },
      },
    }
  })
}

export const getAlertPlotLines = ({ alerts, channels, theme }: { alerts: IAlertHistory[]; channels: ChannelType[]; theme: Theme }) => {
  const chartAlerts = getChartAlerts({ alerts, channels })

  return chartAlerts.flatMap((a) =>
    a.responses
      .filter((r) => r.note === 'ack' || r.note === 'test')
      .map((r) => ({
        color: theme.palette.primary.dark,
        dashStyle: 'ShortDot',
        width: 2,
        zIndex: 4,
        value: new Date(r.updatedAt).getTime(),
        label: {
          text: r.note === 'ack' ? 'Acknowledged' : 'Ignored',
          align: 'right',
          verticalAlign: 'middle',
        },
      }))
  )
}

export const extractSequences = (arr: ChartDataType) => {
  if (arr.length === 0) return []

  const result: SeriesDataObject[] = []
  let currentStart = arr[0][0]

  for (let i = 0; i < arr.length; i += 1) {
    const [x, currentValue] = arr[i]
    const nextElement = arr[i + 1]
    const [nextElementX, nextElementValue] = nextElement || []

    // If the current value is '0', 'LINE', or 'open', treat it as its own group
    if (currentValue === 0 || currentValue === 'LINE' || currentValue === 'open') {
      result.push({ x, x2: x, y: 0, visible: false })
      currentStart = nextElementX || x
    } else if (!nextElement || currentValue !== nextElementValue) {
      result.push({ x: currentStart, x2: nextElement ? nextElementX : x, y: 0, visible: true })
      currentStart = nextElementX || x
    }
  }

  return result
}
